(() => {
  'use strict';
  if (customElements.get('bb-wc-cart-saved')) return;
  const api = 'https://wishlist.webcontrive.com/wishlist-club/public/api/web/get-wishlists';
  const root = window.Shopify?.routes?.root || '/';
  const products = new Map();
  const busy = new Set();
  let saved = [], currentCart = null, pending = null, lastRead = 0, identityKey = '';
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className || '';
    if (text != null) node.textContent = text;
    return node;
  };
  const identity = () => {
    const meta = window.wishlistClubMeta;
    let data;
    try { data = JSON.parse(localStorage.getItem('wishlistClubData') || 'null'); } catch {}
    if (!meta?.wcShop || (!meta.wcCustomerId && !data?.ip)) return null;
    return { customer_id: meta.wcCustomerId, ip: data?.ip || '', shop: meta.wcShop, wishlist_share: null, country: window.Shopify?.country, language: window.Shopify?.locale };
  };
  async function json(url, options = {}) {
    const response = await fetch(url, { ...options, signal: AbortSignal.timeout(12000) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Não foi possível carregar agora.');
    return result;
  }
  async function read(force = false) {
    const owner = identity();
    if (!owner) return false;
    const key = JSON.stringify([owner.shop, owner.customer_id, owner.ip]);
    if (key !== identityKey) { saved = []; lastRead = 0; identityKey = key; }
    if (pending) return pending;
    if (!force && Date.now() - lastRead < 3000 && currentCart) return true;
    pending = Promise.all([
      json(api, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(owner), credentials: 'omit' }),
      json(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' })
    ]).then(([result, cart]) => {
      if (key !== identityKey) return false;
      if (Number(result.status) !== 200 || !Array.isArray(result.data?.saveForLaters) || !Array.isArray(cart.items)) throw new Error('Resposta indisponível.');
      saved = result.data.saveForLaters;
      currentCart = cart;
      lastRead = Date.now();
      return true;
    }).finally(() => { pending = null; });
    return pending;
  }
  function product(handle) {
    if (!products.has(handle)) products.set(handle, json(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' }).catch(error => { products.delete(handle); throw error; }));
    return products.get(handle);
  }
  class Saved extends HTMLElement {
    connectedCallback() {
      this.version = 0;
      this.hidden = true;
      this.draw();
      if (this.closest('cart-drawer')?.hasAttribute('open')) this.refresh();
    }
    disconnectedCallback() { this.version++; this.resizeObserver?.disconnect(); }
    async refresh(force = false) {
      try { if (await read(force)) await this.draw(); }
      catch { if (!this.querySelector('.bb-wc-saved__card')) this.hidden = true; }
    }
    async draw() {
      const version = ++this.version;
      const cartIds = new Set((currentCart?.items || []).map(item => String(item.variant_id)));
      const seen = new Set();
      const eligible = saved.filter(item => {
        const id = String(item.variant_id || '');
        if (!/^\d+$/.test(id) || !item.handle || cartIds.has(id) || seen.has(id)) return false;
        seen.add(id); return true;
      }).slice(0, 12);
      const cards = await Promise.all(eligible.map(async item => {
        try {
          const data = await product(item.handle);
          const variant = data.variants.find(v => String(v.id) === String(item.variant_id));
          if (!variant) return null;
          return this.card(data, variant);
        } catch { return null; }
      }));
      if (!this.isConnected || version !== this.version) return;
      this.resizeObserver?.disconnect();
      this.replaceChildren();
      const valid = cards.filter(Boolean);
      this.hidden = !valid.length;
      if (!valid.length) return;
      const header = element('div', 'bb-wc-saved__header');
      header.append(element('h3', '', `Salvos para depois (${valid.length})`));
      const all = element('a', '', 'Ver todos'); all.href = `${root}apps/wishlist`;
      const tools = element('div', 'bb-wc-saved__tools');
      tools.append(all);
      const arrows = document.querySelector('template[data-bb-saved-arrows]')?.content;
      const previous = arrows?.querySelector('[data-previous]')?.cloneNode(true) || element('button', 'circle-chevron hover:colors bb-wc-saved__arrow', '‹');
      const next = arrows?.querySelector('[data-next]')?.cloneNode(true) || element('button', 'circle-chevron hover:colors bb-wc-saved__arrow', '›');
      previous.type = next.type = 'button';
      previous.setAttribute('aria-label', 'Salvos para depois: anterior');
      next.setAttribute('aria-label', 'Salvos para depois: próximo');
      previous.hidden = next.hidden = valid.length < 2;
      tools.append(previous, next); header.append(tools);
      const list = element('div', 'bb-wc-saved__list');
      list.append(...valid);
      this.status = element('p', 'bb-wc-saved__status');
      this.status.setAttribute('role', 'status');
      this.append(header, list, this.status);
      const sync = () => {
        previous.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
      };
      const step = direction => list.scrollBy({ left: direction * (list.clientWidth + 12), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      previous.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      list.addEventListener('scroll', sync, { passive: true });
      this.resizeObserver = new ResizeObserver(sync); this.resizeObserver.observe(list);
      requestAnimationFrame(sync);
    }
    card(data, variant) {
      const card = element('article', 'bb-wc-saved__card');
      const href = `${root}products/${encodeURIComponent(data.handle)}?variant=${variant.id}`;
      const media = element('a', 'bb-wc-saved__media'); media.href = href; media.tabIndex = -1;
      const image = element('img'); image.alt = data.title; image.width = 88; image.height = 88; image.loading = 'lazy';
      const src = variant.featured_image?.src || data.featured_image;
      if (src) { const url = new URL(src, location.origin); if (url.protocol === 'https:') { url.searchParams.set('width', '176'); image.src = url.href; media.append(image); } }
      const info = element('div', 'bb-wc-saved__info');
      const title = element('a', 'bb-wc-saved__title', data.title); title.href = href;
      info.append(title);
      if (variant.title && variant.title !== 'Default Title') info.append(element('span', 'bb-wc-saved__variant', variant.title));
      const bottom = element('div', 'bb-wc-saved__bottom');
      const prices = element('div', 'bb-wc-saved__prices');
      const payments = window.BlocoboxPayments;
      const money = payments?.money || (value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: data.currency || 'BRL' }).format(value / 100));
      const rate = Number(payments?.pixPercent || 0);
      const main = element('div', 'bb-wc-saved__price');
      main.append(element('strong', '', money(Math.round(variant.price * (100 - rate) / 100))));
      if (rate > 0) main.append(element('span', '', ' no Pix'));
      prices.append(main);
      const terms = payments?.terms(variant.price, false);
      if (terms) prices.append(element('span', 'bb-wc-saved__terms', terms));
      let action;
      if (!variant.available || data.requires_selling_plan) {
        action = element('a', 'bb-wc-saved__add', variant.available ? 'Ver opções' : 'Ver modelo'); action.href = href;
        if (!variant.available) info.append(element('span', 'bb-wc-saved__variant', 'Esgotado'));
      } else {
        action = element('button', 'bb-wc-saved__add', 'Adicionar'); action.type = 'button';
        action.setAttribute('aria-label', `Adicionar ${data.title} dos salvos ao carrinho`);
        action.disabled = busy.has(String(variant.id));
        action.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); this.add(variant, action); });
      }
      bottom.append(prices, action); info.append(bottom); card.append(media, info);
      return card;
    }
    async add(variant, button) {
      const id = String(variant.id);
      if (busy.has(id)) return;
      busy.add(id); button.disabled = true; button.textContent = 'Adicionando…';
      let added = false, attempted = false;
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        attempted = true;
        const result = await json(`${root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname })
        });
        added = true;
        const cart = await json(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!Array.isArray(cart.items)) throw new Error('Confira seu carrinho.');
        cart.sections = result.sections;
        currentCart = cart;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items || [], cart } }));
        document.querySelectorAll('bb-wc-cart-saved').forEach(node => node.draw());
      } catch (error) {
        if (this.status) this.status.textContent = added || attempted ? 'Confira o carrinho antes de tentar novamente.' : error.message;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        button.textContent = 'Confira o carrinho';
      } finally {
        if (added) busy.delete(id);
      }
    }
  }
  let moveDialog = null, pausedCart = null;
  function syncModalFocus() {
    const modal = moveDialog?.open || document.querySelector('.wcWishlistModal[aria-hidden="false"]');
    const drawer = document.querySelector('cart-drawer[open]');
    if (modal && drawer && !pausedCart) {
      pausedCart = drawer;
      drawer.focusTrap?.pause();
    } else if (!modal && pausedCart) {
      const previous = pausedCart; pausedCart = null;
      if (previous.hasAttribute('open')) previous.focusTrap?.unpause();
    }
  }
  new MutationObserver(syncModalFocus).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'open'] });
  function confirmMove(line, trigger) {
    if (moveDialog) return;
    const dialog = element('dialog', 'bb-wc-move-dialog');
    moveDialog = dialog;
    dialog.addEventListener('click', event => event.stopPropagation());
    const heading = element('h2', '', 'Salvar este modelo para depois?'); heading.id = 'bb-wc-move-heading';
    dialog.setAttribute('aria-labelledby', heading.id);
    const name = element('p', 'bb-wc-move-dialog__name', line.querySelector('.bold')?.textContent.trim() || 'Este modelo');
    const description = element('p', '', 'Ele sairá do carrinho e ficará em Salvos para depois.');
    const feedback = element('p', 'bb-wc-move-dialog__feedback'); feedback.setAttribute('role', 'status');
    const actions = element('div', 'bb-wc-move-dialog__actions');
    const cancel = element('button', '', 'Cancelar'); cancel.type = 'button';
    const save = element('button', '', 'Salvar para depois'); save.type = 'button';
    const close = element('button', 'bb-wc-move-dialog__close', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Fechar confirmação');
    let processing = false;
    const dismiss = () => { if (!processing) dialog.close(); };
    cancel.addEventListener('click', dismiss); close.addEventListener('click', dismiss);
    dialog.addEventListener('cancel', event => { if (processing) event.preventDefault(); });
    dialog.addEventListener('close', () => { dialog.remove(); moveDialog = null; syncModalFocus(); document.querySelector('cart-drawer')?.show(); if (trigger.isConnected) trigger.focus(); });
    save.addEventListener('click', async () => {
      if (processing) return;
      processing = true; save.disabled = cancel.disabled = close.disabled = true; save.textContent = 'Salvando…'; feedback.textContent = '';
      const key = trigger.dataset.bbSaveLine;
      let stored = false;
      try {
        const owner = identity();
        if (!owner) throw new Error('Aguarde o carregamento dos favoritos e tente novamente.');
        const cart = await json(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        const item = cart.items?.find(item => item.key === key);
        if (!item) throw new Error('Este modelo já saiu do carrinho.');
        let data; try { data = JSON.parse(localStorage.getItem('wishlistClubData') || 'null'); } catch {}
        await read(true);
        stored = saved.some(savedItem => String(savedItem.variant_id) === String(item.variant_id));
        if (!stored) {
          const result = await json('https://wishlist.webcontrive.com/wishlist-club/public/api/web/save-for-later', {
            method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify({ customer_id: owner.customer_id, ip: owner.ip || null, shop: owner.shop, type: 'remove-cart', product_id: item.product_id, variant_id: item.variant_id, wishlist_id: data?.wishlist?.[0]?.id || null })
          });
          if (Number(result.status) !== 200) throw new Error('Não foi possível salvar. O produto continua no carrinho.');
          stored = true;
        }
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const updated = await json(`${root}cart/update.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [key]: 0 }, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname })
        });
        if (!Array.isArray(updated.items) || updated.items.some(item => item.key === key)) throw new Error('Confira o carrinho.');
        currentCart = updated;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'bb:save-for-later', cart: updated } }));
        await read(true);
        dialog.close();
        document.querySelectorAll('bb-wc-cart-saved').forEach(node => node.draw());
        document.querySelector('cart-drawer')?.show();
      } catch (error) {
        feedback.textContent = stored ? 'O modelo foi salvo no WC. Confira o carrinho antes de tentar novamente.' : error.message;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } finally {
        processing = false; save.disabled = cancel.disabled = close.disabled = false; save.textContent = 'Salvar para depois';
      }
    });
    actions.append(cancel, save); dialog.append(close, heading, name, description, feedback, actions); document.body.append(dialog); dialog.showModal(); syncModalFocus(); cancel.focus();
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-bb-save-line]');
    if (!button) return;
    event.preventDefault(); event.stopPropagation();
    const line = button.closest('line-item');
    if (line && line.querySelector('bb-cart-quantity')?.getAttribute('aria-busy') !== 'true') confirmMove(line, button);
  }, true);
  customElements.define('bb-wc-cart-saved', Saved);
  const refresh = force => document.querySelectorAll('bb-wc-cart-saved').forEach(node => node.refresh(force));
  document.addEventListener('dialog:after-show', event => { if (event.target.matches('cart-drawer')) refresh(true); });
  document.addEventListener('cart:change', event => {
    if (Array.isArray(event.detail?.cart?.items)) currentCart = event.detail.cart;
    document.querySelectorAll('bb-wc-cart-saved').forEach(node => node.draw());
  });
  document.addEventListener('bb:cart-rendered', () => refresh(false));
  document.addEventListener('click', event => {
    if (event.target.closest('.wcWishlistModal_footer button')) {
      setTimeout(() => refresh(true), 600);
      setTimeout(() => refresh(true), 1800);
    }
  }, true);
  window.addEventListener('pageshow', () => { if (document.querySelector('cart-drawer[open]')) refresh(true); });
})();
